import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Bundle, Organization } from 'fhir/r4';
import { backendEndPointToken } from '../app.config';
import { ErrorService } from '../shared/error.service';
import { AuthService as Auth0Service} from '@auth0/auth0-angular';
import { combineLatest } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LoaderComponent } from '../loader/loader.component';
import { AuthService } from '../shared/auth/auth.service';

@Component({
  selector: 'app-hospital-selection-login',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatChipsModule
  ],
  templateUrl: './hospital-selection-login.component.html',
  styleUrls: ['./hospital-selection-login.component.scss']
})
export class HospitalSelectionLoginComponent implements OnInit {
  private http = inject(HttpClient);
  private router = inject(Router);
  private backendUrl = inject(backendEndPointToken);
  private errorService = inject(ErrorService);

  hospitals: Organization[] = [];
  loading = true;
  selectedHospitalId: string | null = null;
  currentYear = new Date().getFullYear(); // ✅ Add current year for footer
  alreadyLoggedInHospitalId?:string;
  ngOnInit(): void {
    this.loadHospitals();
    this.auth0Service.user$.subscribe(user=>{
        if(user){
            console.log(user);
            this.alreadyLoggedInHospitalId = user?.['https://elikita-server.daalitech.com/hospitalId'];

        }
    })
  }

  /**
   * Fetch all registered hospitals/organizations from the backend
   */
   loadHospitals(): void {
    this.loading = true;
    
    this.http.get<Bundle<Organization>>(`${this.backendUrl}/Organization?_count=100`)
      .subscribe({
        next: (bundle) => {
          this.hospitals = (bundle.entry || [])
            .map(entry => entry.resource as Organization)
            .filter(org => org.active !== false); // Only show active organizations
          
          console.log(`✅ Loaded ${this.hospitals.length} hospitals`);
          this.loading = false;
        },
        error: (err) => {
          console.error('❌ Failed to load hospitals:', err);
          this.errorService.openandCloseError('Failed to load hospitals. Please try again.');
          this.loading = false;
        }
      });
  }

  /**
   * Navigate to login page for selected hospital
   */
  auth0Service = inject(Auth0Service);
  auth = inject(AuthService);
  dialog = inject(MatDialog)
//   router = inject(R)
  onSelectHospital(hospital: Organization): void {
    if (!hospital.id) {
      this.errorService.openandCloseError('Invalid hospital selection');
      return;
    }
// $#$// /* alert(hospital.id); */
    console.log('Selected hospital:', hospital);
    this.selectedHospitalId = hospital.id;

    // Store selected hospital ID in localStorage for the login flow
    localStorage.setItem('selectedHospitalId', hospital.id);
    localStorage.setItem('selectedHospitalName', hospital.name || 'Hospital');
    combineLatest([
        this.auth0Service.isLoading$,
        this.auth0Service.isAuthenticated$,
        this.auth0Service.user$,
        

    ]).subscribe(([isLoading, isAuthenticated, user]) => {
        let dref:MatDialogRef<LoaderComponent> | undefined;
       
        if(isLoading){
            dref= this.dialog.open(LoaderComponent);
        }else{
            if(dref){
                dref.close();
            }
             if(isAuthenticated && user){
                if(user['https://elikita-server.daalitech.com/hospitalId'] === hospital.id){
            const target= "/app/dashboard"
             const current = this.auth?.user?.getValue();
    if (!current || current.role !== 'cashier') {
      // Ensure login redirects to our target route
      this.auth.triedUrl = target;
      this.auth.login('cashier123', 'cashier123');
    } else {
      // Already a cashier: navigate directly
      this.router.navigateByUrl(target);
    }}else{
        this.auth0Service.loginWithRedirect({
                authorizationParams:{
                    audience:"https://elikita-server-proxy.daalitech.com",
                    scope:"patient:readwrite condition:write",
                    "ext-hospitalId": hospital.id
                }
            }); 
    }
        }else{
            this.auth0Service.loginWithRedirect({
                authorizationParams:{
                    audience:"https://elikita-server-proxy.daalitech.com",
                    scope:"patient:readwrite condition:write",
                    "ext-hospitalId": hospital.id
                }
            });
        }
        }

    
  

        
    });
    // Navigate to the actual login page with hospital context
    // this.router.navigate(['/login'], { 
    //   queryParams: { hospitalId: hospital.id } 
    // });
  }

  /**
   * Get formatted address from Organization
   */
  getHospitalAddress(hospital: Organization): string {
    if (!hospital.address || hospital.address.length === 0) {
      return 'Address not available';
    }

    const address = hospital.address[0];
    const parts: string[] = [];

    if (address.line && address.line.length > 0) {
      parts.push(address.line.join(', '));
    }
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.country) parts.push(address.country);

    return parts.length > 0 ? parts.join(', ') : 'Address not available';
  }

  /**
   * Get hospital type/category for display
   */
  getHospitalType(hospital: Organization): string {
    if (!hospital.type || hospital.type.length === 0) {
      return 'Healthcare Facility';
    }

    const type = hospital.type[0];
    return type.text || 
           type.coding?.[0]?.display || 
           type.coding?.[0]?.code || 
           'Healthcare Facility';
  }

  /**
   * Get hospital contact phone number
   */
  getHospitalPhone(hospital: Organization): string | null {
    if (!hospital.telecom || hospital.telecom.length === 0) {
      return null;
    }

    const phoneContact = hospital.telecom.find(t => t.system === 'phone');
    return phoneContact?.value || null;
  }

  /**
   * Get hospital contact email
   */
  getHospitalEmail(hospital: Organization): string | null {
    if (!hospital.telecom || hospital.telecom.length === 0) {
      return null;
    }

    const emailContact = hospital.telecom.find(t => t.system === 'email');
    return emailContact?.value || null;
  }

  /**
   * Check if hospital is currently selected
   */
  isSelected(hospital: Organization): boolean {
    return this.selectedHospitalId === hospital.id;
  }
}
